import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { listDuplicateDays, removeDuplicateDays } from "@/lib/api";

function fmtDate(d) {
  if (!d) return "—";
  const [y, m, dd] = d.split("-");
  return `${dd}/${m}/${y}`;
}

/**
 * Warns when several dates hold exactly the same cuts (the CRM returned the
 * table of another day) and lets the user delete the copies.
 */
export default function DuplicateDaysPanel({ refreshKey, onChanged }) {
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState({});
  const [removing, setRemoving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await listDuplicateDays();
      setGroups(r.groups || []);
    } catch {
      setGroups([]);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  function openReview() {
    const sel = {};
    groups.forEach((g) => g.days.forEach((d) => { sel[d.date] = true; }));
    setSelected(sel);
    setOpen(true);
  }

  const chosen = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const totalDates = groups.reduce((a, g) => a + g.days.length, 0);

  async function onRemove() {
    if (!chosen.length) return;
    setRemoving(true);
    try {
      const r = await removeDuplicateDays(chosen);
      toast.success(`${r.deleted_count} dia(s) apagado(s). Volte a recolher esses dias na rotina (botão "Dias").`);
      setOpen(false);
      await load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao apagar dias");
    } finally {
      setRemoving(false);
    }
  }

  if (!groups.length) return null;

  return (
    <>
      <div className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="po-duplicate-banner">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
        <div className="flex-1 text-sm">
          <div className="font-semibold">{totalDates} dias têm exatamente os mesmos cortes</div>
          <div className="text-amber-800/90">
            O CRM devolveu a tabela de outro dia e os cortes ficaram repetidos em várias datas. Os totais das ordens de fabrico estão inflacionados.
          </div>
        </div>
        <Button variant="outline" className="border-amber-400 bg-white hover:bg-amber-100" onClick={openReview} data-testid="po-duplicate-review-button">
          Rever dias repetidos
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="po-duplicate-dialog">
          <DialogHeader>
            <DialogTitle>Dias com cortes repetidos</DialogTitle>
            <DialogDescription>
              Cada grupo tem os mesmos cortes, ao segundo, em datas diferentes. Não é possível saber qual a data verdadeira, por isso estão todos marcados.
              Desmarque o dia que quer manter, se souber qual é. Depois de apagar, volte a recolher esses dias na rotina (botão “Dias”).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {groups.map((g, gi) => (
              <div key={g.fingerprint} className="rounded-lg border" data-testid={`po-duplicate-group-${gi}`}>
                <div className="px-3 py-2 bg-muted/50 text-xs text-muted-foreground border-b">
                  Grupo {gi + 1} · {g.records_count} cortes iguais em {g.days.length} dias
                </div>
                <div className="divide-y">
                  {g.days.map((d) => (
                    <label key={d.date} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-muted/30">
                      <Checkbox
                        checked={!!selected[d.date]}
                        onCheckedChange={(v) => setSelected((s) => ({ ...s, [d.date]: !!v }))}
                        data-testid={`po-duplicate-check-${d.date}`}
                      />
                      <span className="font-medium tabular-nums w-24">{fmtDate(d.date)}</span>
                      <span className="text-xs text-muted-foreground">
                        {d.source === "scraper_bridge" ? "recolha automática" : "importação manual"}
                        {d.uploaded_at ? ` · gravado ${new Date(d.uploaded_at).toLocaleString("pt-PT")}` : ""}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={onRemove} disabled={!chosen.length || removing} data-testid="po-duplicate-remove-button">
              {removing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Apagar {chosen.length} dia(s)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
